import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { getAxiosInstance } from "../../../utils/axiosInstance";
import { Transfer } from "../../../utils/transferTypes";
import Entry from "./Entry";
import { Divider, Spinner } from "@chakra-ui/react";
import { GiCardboardBox } from "react-icons/gi";
import dayjs from "dayjs";
import SelectCompanyAndPlaceOfBusiness from "../../../components/SelectCompanyAndPlaceOfBusiness";

interface ToTransfers {
    reloadFlag: boolean;
    setReloadFlag: (data: boolean) => void;    
}

const ToTransfers = (props: ToTransfers) => {
    const { t } = useTranslation("translations");
    const [entries, setEntries] = useState<Transfer[]>([]);
    const [loadingData, setLoadingData] = useState<boolean>(false);

    const [companyId, setCompanyId] = useState<string | undefined>(undefined);
    const [placeOfBusinessId, setPlaceOfBusinessId] = useState<string | undefined>(undefined);

    const fetch = async () => {
        setLoadingData(true);
        try {
            const res = await getAxiosInstance().get("/Transfer/To", {
                params: {
                    PageNumber: 1,
                    PageSize: 500,
                    companyId,
                    placeOfBusinessId
                }
            });
   
            setEntries([...res.data]);
        } catch (e) {
            toast.error(t('FETCH_DATA_ERROR'), { position: "bottom-right" });
        } finally {
            setLoadingData(false);
        }
    }

    const deny = async (id: string) => {
        try {
            await getAxiosInstance().put(`/Transfer/Deny/${id}`);
            toast.success(t('TRANSFER_DENY_SUCCESSFULLY'), { position: "bottom-right" });
        } catch (e) {
            toast.error(t('TRANSFER_DENY_FAIL'), { position: "bottom-right" });
        } finally {
            fetch();
            props.setReloadFlag(!props.reloadFlag);
        }
    }

    const accept = async (id: string) => {
        try {
            await getAxiosInstance().put(`/Transfer/Accept/${id}`);
            toast.success(t('TRANSFER_ACCEPT_SUCCEEDED'), { position: "bottom-right" });
        } catch (e) {
            toast.error(t('TRANSFER_ACCEPT_FAIL'), { position: "bottom-right" });
        } finally {
            fetch();
            props.setReloadFlag(!props.reloadFlag);
        }
    }

    useEffect(() => {
        fetch().then();
    }, [props.reloadFlag]);

    useEffect(() => {
        fetch().then();
    }, [companyId, placeOfBusinessId]);

    return <div className="mt-16">
        <div className="ml-4 text-xl font-bold">{t("TRANSFER_TO_ENTRIES")}</div>
        <Divider className="mb-4 mx-4"/>
        <div className="w-full m-2 p-2 flex md:flex-row flex-col">
            <SelectCompanyAndPlaceOfBusiness
                onChangeCompany={(v) => setCompanyId(v)}
                valueCompany={companyId}
                onChangePlaceOfBusiness={(v) => setPlaceOfBusinessId(v)}
                valuePlaceOfBusiness={placeOfBusinessId}
                companyFiledName={t("FROM_COMPANY_FILTER_TRANSFER")}
                placeOfBusinessName={t("FROM_PLACE_OF_BUSINESS_FILTER_TRANSFER")}
            />
        </div>
        <div className="h-[300px] overflow-y-auto
[&::-webkit-scrollbar]:w-2
[&::-webkit-scrollbar-track]:rounded-full
[&::-webkit-scrollbar-track]:bg-gray-100
[&::-webkit-scrollbar-thumb]:rounded-full
[&::-webkit-scrollbar-thumb]:bg-gray-300
dark:[&::-webkit-scrollbar-track]:bg-slate-700
dark:[&::-webkit-scrollbar-thumb]:bg-slate-500 relative"
        >
            {
                entries.map((e, index) => (
                    <Entry
                        key={`to_transfer_entry_${index}`}
                        from={e.fromName}
                        to={e.toName}
                        product={e.productName}
                        quantity={e.quantity}
                        um={e.um}
                        cancelType={"DECLINE"}
                        cancelAction={() => deny(e.id)}
                        accept="ACCEPT"
                        acceptAction={() => accept(e.id)}
                        createdAt={dayjs(e.createdAt).format("DD-MM-YYYY HH:mm:ss")}
                        createdBy={e.createdBy}
                        id={e.id}
                    />
                ))
            }
            {
                !loadingData && entries.length === 0 &&
                <div className="w-full my-[20px] opacity-60">
                    <GiCardboardBox className="w-[250px] h-[200px] mx-auto opacity-80"/>
                    <div className="text-center">{t("NO_ENTRY_TO_TRANSFER")}</div>
                </div>
            }
            {
                loadingData &&
                <div
                    className="w-full h-full absolute bg-gray-500/20 flex justify-center items-center top-0 left-0">
                    <Spinner
                        thickness='4px'
                        speed='0.65s'
                        emptyColor='gray.200'
                        color='blue.500'
                        size='xl'
                    />
                </div>
            }
        </div>
    </div>
}

export default ToTransfers;